import { Button, Link, Tooltip } from '@raisin/components-lib';
import { useT } from '@transifex/react';
import messages from 'messages';
import React, { FC } from 'react';
import { ProductOfferListItem } from 'types';
import * as S from '../styles';
import * as DesktopStyles from './styles';
import Info from '@raisin/components-lib/Icons/Info';

import { FinancialInstitutions } from '../FinancialInstitutions/FinancialInstitutions';
import { getInstitutionChipLabels } from '../utils';

interface DesktopItemProps {
  offer: ProductOfferListItem;
  earnings: string;
  loadingProductId: string;
  bankLogoLink?: string;
  apyDisplay: string;
  maturityDisplay: string;
  isSavingsOffer: boolean;
  openProductTerms: (productId: string) => void;
  isCallableCD: boolean;
  onInvest: (productId: string) => void;
}

export const DesktopItem: FC<DesktopItemProps> = ({
  offer: {
    id,
    bankLogo,
    bankName,
    productName,
    financialInstitutions,
    teaserPeriodInDays,
    teaserStatus,
  },
  earnings,
  bankLogoLink,
  loadingProductId,
  apyDisplay,
  maturityDisplay,
  isSavingsOffer,
  openProductTerms,
  isCallableCD,
  onInvest,
}) => {
  const t = useT();
  const logoElement = <S.BankLogo src={bankLogo} alt={bankName} />;
  const isPromo = teaserStatus === 'ACTIVE';

  return (
    <DesktopStyles.DesktopItemContainer data-testid="desktop-item" $isPromo={isPromo}>
      {isPromo && (
        <S.PromoChip data-testid="promoBadge">
          <S.Text variant="subtitle1" $color={'white'}>
            {t(messages.usOfferTableTeaserBadge, { teaserPeriodInDays })}
          </S.Text>
        </S.PromoChip>
      )}
      <DesktopStyles.ProductDetailsRow $isSavingsOffer={isSavingsOffer} elevation={1}>
        <DesktopStyles.DesktopItem $isFlex>
          <S.BankLogoContainer $flex="0 0 30%">
            {bankLogoLink ? <a href={bankLogoLink}>{logoElement}</a> : logoElement}
          </S.BankLogoContainer>
          <S.TruncatedText variant="subtitle1" $flex="0 0 65%" title={bankName}>
            {bankName}
          </S.TruncatedText>
        </DesktopStyles.DesktopItem>
        <DesktopStyles.DesktopItem>
          <S.Text variant="subtitle1" $color={isCallableCD ? 'blue40' : 'black100'}>
            {productName}
          </S.Text>
          <div>
            <Link
              style={{
                color: loadingProductId === id && 'grey',
                cursor: loadingProductId === id && 'wait',
              }}
              onClick={() => loadingProductId !== id && openProductTerms(id)}
            >
              {t(messages.usOfferTableProductTerms)}
            </Link>
          </div>
        </DesktopStyles.DesktopItem>
        <DesktopStyles.DesktopItem $isFlex={isPromo}>
          <S.BlueText variant="h3">{apyDisplay}</S.BlueText>
          {isPromo && (
            <Tooltip
              content={t(messages.usOfferTableTeaserTooltip, { apyDisplay, teaserPeriodInDays })}
            >
              <S.TooltipButton data-testid="promoTooltip">
                <Info size="md" color={'primary'} />
              </S.TooltipButton>
            </Tooltip>
          )}
        </DesktopStyles.DesktopItem>
        {!isSavingsOffer && (
          <DesktopStyles.DesktopItem>
            <S.Text variant="subtitle1">{maturityDisplay}</S.Text>
          </DesktopStyles.DesktopItem>
        )}
        <DesktopStyles.DesktopItem>
          <S.Text variant="subtitle1">{earnings}</S.Text>
        </DesktopStyles.DesktopItem>

        <DesktopStyles.DesktopItem $textAlignRight>
          <Button fullWidth onClick={() => onInvest(id)}>
            {t(messages.usOfferTableSaveButton)}
          </Button>
        </DesktopStyles.DesktopItem>
      </DesktopStyles.ProductDetailsRow>
      <FinancialInstitutions
        financialInstitutions={getInstitutionChipLabels(financialInstitutions)}
      />
    </DesktopStyles.DesktopItemContainer>
  );
};
